<template>
    <div class="background-small">
        <section class="privacy-container">
            <h1 class="privacy-title">Privacy Policy</h1>
            
            <div class="privacy-content">
                <section v-for="(section, index) in privacySections" :key="index" class="policy-section">
                    <h2>{{ section.title }}</h2>
                    <p v-if="section.description">{{ section.description }}</p>
                    <ul v-if="section.items">
                        <li v-for="(item, itemIndex) in section.items" :key="itemIndex">{{ item }}</li>
                    </ul>
                    <div v-if="section.contact" class="contact-info">
                        <p v-for="(info, infoIndex) in section.contact" :key="infoIndex">{{ info }}</p>
                    </div>
                </section>
            </div>
        </section>
    </div>
</template>

<script setup>
const privacySections = [
    {
        title: "1. Information We Collect",
        description: "We collect information that you provide directly to us, including:",
        items: [
            "Account information (name, email address)",
            "Profile information (profile picture, bio)",
            "Transaction information",
            "Communication preferences"
        ]
    },
    {
        title: "2. How We Use Your Information",
        description: "We use the information we collect to:",
        items: [
            "Provide, maintain, and improve our services",
            "Process transactions and send related information",
            "Send you technical notices and support messages",
            "Respond to your comments and questions",
            "Monitor and analyze trends and usage"
        ]
    },
    {
        title: "3. Information Sharing",
        description: "We do not share your personal information with third parties except in the following circumstances:",
        items: [
            "With your consent",
            "To comply with legal obligations",
            "To protect our rights and prevent fraud",
            "With service providers who assist in our operations"
        ]
    },
    {
        title: "4. Data Security",
        description: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction."
    },
    {
        title: "5. Your Rights",
        description: "You have the right to:",
        items: [
            "Access your personal information",
            "Correct inaccurate data",
            "Request deletion of your data",
            "Object to processing of your data",
            "Data portability"
        ]
    },
    {
        title: "6. Cookies and Tracking",
        description: "We use cookies and similar tracking technologies to track activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent."
    },
    {
        title: "7. Changes to This Policy",
        description: "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the \"Last Updated\" date."
    },
    {
        title: "8. Contact Us",
        description: "If you have any questions about this Privacy Policy, please contact us at:",
        contact: [
            "Email: admin@steamanalyzer.com",
            "Last Updated: May 2025"
        ]
    }
];
</script>

<style scoped>
.privacy-wrapper {
    min-height: 100vh;
    background: linear-gradient(135deg, #1a1c2a 0%, #2a2d3e 100%);
    padding: 2rem 0;
}

.privacy-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.privacy-title {
    font-size: 2.5rem;
    text-align: center;
    margin-bottom: 2rem;
    font-weight: 700;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.privacy-content {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 2rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.policy-section {
    margin-bottom: 2.5rem;
    color: rgba(255, 255, 255, 0.9);
}

.policy-section:last-child {
    margin-bottom: 0;
}

.policy-section h2 {
    color: white;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
}

.policy-section p {
    line-height: 1.6;
    margin-bottom: 1rem;
}

.policy-section ul {
    list-style-type: none;
    padding-left: 1.5rem;
    margin-bottom: 1rem;
}

.policy-section ul li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.5rem;
    line-height: 1.6;
}

.policy-section ul li::before {
    content: "•";
    position: absolute;
    left: 0;
    color: #8650ac;
}

.contact-info {
    background: rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
    border-radius: 8px;
    margin-top: 1rem;
}

.contact-info p {
    margin-bottom: 0.5rem;
}

.contact-info p:last-child {
    margin-bottom: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
}

@media (max-width: 768px) {
    .privacy-container {
        padding: 1rem;
    }

    .privacy-title {
        font-size: 2rem;
        margin-bottom: 2rem;
    }

    .privacy-content {
        padding: 1.5rem;
    }

    .policy-section h2 {
        font-size: 1.3rem;
    }
}
</style> 